import { useEffect, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toaster } from "../../utils/toast";
import { useTranslation } from "react-i18next";
import { useLanguageDirection } from "../../hooks";
import useAuthStore from "../../store/authStore";
import { SECURITY_CONFIG } from "../../security/securityConfig";

export const useIdleTimeout = () => {
  const { dir } = useLanguageDirection();
  const navigate = useNavigate();
  const { t } = useTranslation("userTranslation");
  const { resetIdleTimeout, isAuthenticated, rememberMe, logout } =
    useAuthStore();

  const [showWarning, setShowWarning] = useState(false);
  const [timeUntilTimeout, setTimeUntilTimeout] = useState(null);

  const handleUserActivity = useCallback(() => {
    if (isAuthenticated && !rememberMe) {
      resetIdleTimeout();
      setShowWarning(false);
    }
  }, [isAuthenticated, rememberMe, resetIdleTimeout]);

  const handleTimeout = useCallback(async () => {
    setShowWarning(false);
    await logout();
    toaster(
      "warning",
      t(
        "auth.sessionExpiredIdle",
        "Your session has expired due to inactivity"
      ),
      dir
    );
    navigate("/login", { replace: true });
  }, [logout, navigate, t]);

  const extendSession = useCallback(() => {
    handleUserActivity();
    setShowWarning(false);
  }, [handleUserActivity]);

  useEffect(() => {
    // Only run idle timeout if user is authenticated and rememberMe is false
    if (!isAuthenticated || rememberMe) {
      setShowWarning(false);
      setTimeUntilTimeout(null);
      return;
    }

    const checkIdleTimeout = () => {
      const storeLastActivity = useAuthStore.getState().lastActivity;
      if (storeLastActivity == null) {
        resetIdleTimeout();
        return;
      }

      const timeSinceLastActivity = Date.now() - storeLastActivity;
      const timeLeft =
        SECURITY_CONFIG.SESSION.IDLE_TIMEOUT - timeSinceLastActivity;

      setTimeUntilTimeout(Math.max(0, timeLeft));

      if (timeLeft <= SECURITY_CONFIG.SESSION.TIMEOUT_WARNING && timeLeft > 0) {
        setShowWarning(true);
      } else if (timeLeft <= 0) {
        handleTimeout();
      } else {
        setShowWarning(false);
      }
    };

    // Initial check
    checkIdleTimeout();

    // Set up interval to check session
    const interval = setInterval(
      checkIdleTimeout,
      SECURITY_CONFIG.SESSION.CHECK_INTERVAL
    );

    // Events to track user activity
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, handleUserActivity, true);
    });

    // Cleanup
    return () => {
      clearInterval(interval);
      events.forEach((event) => {
        document.removeEventListener(event, handleUserActivity, true);
      });
    };
  }, [isAuthenticated, rememberMe, handleUserActivity, handleTimeout]);

  return {
    handleUserActivity,
    showWarning,
    timeUntilTimeout,
    extendSession,
  };
};
